import React, {useState, useEffect, useRef} from 'react';
import {IShowsRail, ITile} from '@dazn/lp-web/src/types';
import {Swiper, SwiperSlide} from 'swiper/react';
import fetchRails from '@dazn/lp-web/src/services/rail.service';
import {showsRailProxy} from '@dazn/lp-web/src/proxies';
import styles from '@dazn/lp-web/src/components/ShowsRail/shows-rail.module.css';
import {animated} from '@react-spring/web';
import {useTitleTransitions} from '@dazn/lp-web/src/hooks/useTransitions';
import {useMedia} from '@dazn/lp-web/src/hooks/useMedia';
import {ComponentTypes} from '@dazn/lp-web/src/constants/componentTypes.constants';
import classnames from 'classnames';
import {
    triggerElementClick,
    triggerViewItemList
} from '@dazn/lp-web/src/analytics/fe-events';
import {elementClickEventTypes} from '@dazn/lp-web/src/analytics/fe-events/elementClick';
import IconChevron from '@dazn/lp-web/src/components/Rail/Chevron';
import {
    getRailSlidesPerView,
    railSliderConfig
} from '@dazn/lp-web/src/utils/railConfig';
import ShowsTile from '@dazn/lp-web/src/components/ShowsRail/ShowsTile';

interface IShowsRailProps {
    data: IShowsRail;
    key: number;
    disableAnimation: boolean;
}

const ShowsRail: React.FC<IShowsRailProps> = ({
    data: showsRail,
    disableAnimation
}) => {
    const [rail, setRail] = useState<IShowsRail>(showsRail);

    const swiperRef = useRef<any>();
    const [refView, springs] = useTitleTransitions();
    const [showPrev, setShowPrev] = useState<boolean>(false);
    const [showNext, setShowNext] = useState<boolean>(false);
    const {isDesktop, isMobile, isTab} = useMedia();
    const [activeIndex, setActiveIndex] = useState<number>(0);

    const [isAtEnd, setIsAtEnd] = useState<boolean>(false);
    const [isAtStart, setIsAtStart] = useState<boolean>(true);

    useEffect(() => {
        if (swiperRef.current) {
            setShowPrev(isDesktop && swiperRef.current.allowSlidePrev);
            setShowNext(isDesktop && swiperRef.current.allowSlideNext);
        }
    }, [swiperRef.current]);

    useEffect(() => {
        if (rail?.tiles && rail?.tiles?.length > 0) {
            const tileImpressions = rail?.tiles?.map((tile, index) => ({
                tileTitle: tile.title || '',
                assetId: tile.assetId || '',
                assetType: 'fixture',
                tilePositionOnLoad: index + 1,
                tilePositionOnView: index + 1,
                numberOfTilesInRail: rail?.tiles.length,
                entitlementId:
                    Array.isArray(tile.entitlementIds) &&
                    tile.entitlementIds?.length > 0
                        ? tile.entitlementIds[0]
                        : '',
                entitlementIds: tile.EntitlementIds,
                railTitle: '',
                railId: '',
                railPosition: '',
                competitionId: tile.competitionId || '',
                competitionName: tile.competitionName || ''
            }));
            triggerViewItemList({
                tileImpressions,
                scheduleDate: '',
                scheduleFilter: '',
                impressionName: 'rail impression',
                type: 'rail'
            });
        }
    }, [rail?.tiles, rail?.componentType]);

    const handlePrev = () => {
        swiperRef.current?.slidePrev();
        setActiveIndex(swiperRef.current?.activeIndex ?? 0);
    };

    const handleNext = () => {
        swiperRef.current?.slideNext();
        setActiveIndex(swiperRef.current?.activeIndex ?? 0);
    };

    useEffect(() => {
        if (swiperRef.current) {
            setIsAtEnd(swiperRef.current.isEnd);
            setIsAtStart(swiperRef.current.isBeginning);
        }
    }, [activeIndex]);

    const slidesPerView = getRailSlidesPerView(window.innerWidth);

    const animations = disableAnimation ? {} : {style: springs, ref: refView};

    const showSeeMore = rail.tiles.length >= slidesPerView;
    const centerRailDetails =
        rail.componentType === ComponentTypes.STANDARD_RAIL
            ? false
            : rail.tiles.length < slidesPerView;

    const handleSeeMoreButton = () => {
        triggerElementClick({
            section: elementClickEventTypes.BODY,
            itemId: 'see_more',
            resourceString: rail?.button?.label || ''
        });
        if (rail?.button?.action) {
            rail?.button?.action();
        }
    };

    useEffect(() => {
        (async function () {
            try {
                const result = await fetchRails([
                    {
                        railId: showsRail.railId,
                        railParams: showsRail.railParams
                    }
                ]);
                setRail(showsRailProxy(showsRail, result?.[0]));
            } catch (err) {
                console.log(err);
            }
        })();
    }, []);

    if (!rail || !rail.tiles.length) {
        return null;
    }

    return (
        <div className={styles.showsRail}>
            <animated.div {...animations}>
                <div className={styles.railHeader}>
                    <div
                        className={classnames(styles.railTitleSection, {
                            [styles.centerRailDetails]:
                                centerRailDetails && isDesktop
                        })}
                    >
                        <h1 className={styles.railTitle}>{rail.title}</h1>
                        {rail?.description && (
                            <p className={styles.railSubTitle}>
                                {rail?.description}
                            </p>
                        )}
                    </div>
                </div>
                {isDesktop && (
                    <div className={styles.railActions}>
                        {rail?.button?.label &&
                            (isDesktop || isTab) &&
                            showSeeMore && (
                                <button
                                    className={styles.seeMore}
                                    onClick={handleSeeMoreButton}
                                >
                                    {rail?.button?.label}
                                </button>
                            )}
                        {showPrev && (
                            <div
                                className={classnames(styles.prev, {
                                    [styles.disable]: isAtStart
                                })}
                                onClick={handlePrev}
                            >
                                <IconChevron />
                            </div>
                        )}
                        {showNext && (
                            <div
                                className={classnames(styles.next, {
                                    [styles.disable]: isAtEnd
                                })}
                                onClick={handleNext}
                            >
                                <IconChevron />
                            </div>
                        )}
                    </div>
                )}
            </animated.div>
            {isDesktop && (
                <Swiper
                    direction="horizontal"
                    speed={500}
                    spaceBetween={20}
                    onSwiper={(swiper: any) => {
                        swiperRef.current = swiper;
                    }}
                    breakpoints={railSliderConfig}
                    centerInsufficientSlides={centerRailDetails}
                >
                    {rail?.tiles?.map((tile: ITile, index: number) => {
                        return (
                            <SwiperSlide key={index}>
                                <ShowsTile
                                    tile={tile as ITile}
                                    index={index}
                                    slidesPerView={slidesPerView}
                                    key={index}
                                    disableAnimation={disableAnimation}
                                />
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            )}
            {(isMobile || isTab) && (
                <div className={styles.railSlider}>
                    {rail?.tiles?.map((tile: ITile, index: number) => (
                        <ShowsTile
                            tile={tile as ITile}
                            index={index}
                            slidesPerView={slidesPerView}
                            key={index}
                            disableAnimation={disableAnimation}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ShowsRail;
